import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  MonthlySpendReport,
  MonthlySpendReportScope,
  OpenAIPlatformKeySpend,
} from '@internal/plugin-ai-spend-monitor-common';
import type {
  AiSpendMonitorApi,
  MonthlySpendReportRequestOptions,
  UserSpend,
} from './types';

export class AiSpendMonitorClient implements AiSpendMonitorApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  async getMonthlyReport(
    month: string,
    scope: MonthlySpendReportScope,
    selectedUserEmails: readonly string[] = [],
    options: MonthlySpendReportRequestOptions = {},
  ): Promise<MonthlySpendReport> {
    const baseUrl =
      await this.options.discoveryApi.getBaseUrl('ai-spend-monitor');
    const query = new URLSearchParams({ month });
    if (scope === 'people') {
      query.set('scope', scope);
      selectedUserEmails.forEach(userEmail => query.append('user', userEmail));
    } else {
      query.set('audience', scope);
    }
    if (options.includeAllSpenders) {
      query.set('ninja', '');
    }
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/reports/monthly?${query.toString()}`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json() as Promise<MonthlySpendReport>;
  }

  async getMySpend(): Promise<UserSpend | undefined> {
    const baseUrl =
      await this.options.discoveryApi.getBaseUrl('ai-spend-monitor');
    const response = await this.options.fetchApi.fetch(`${baseUrl}/me`);

    // 404 = nothing to show for this caller yet. The backend returns it both
    // when no spend snapshot has been collected (a brand-new user, or before
    // the first collection run) and when it cannot resolve the caller's
    // identity/email (`spend_identity_unavailable`). Both are normal empty
    // states on a shared home page, not errors — return undefined and let the
    // card render quietly.
    if (response.status === 404) {
      return undefined;
    }
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json() as Promise<UserSpend>;
  }

  async getOpenAIPlatformKeySpend(
    month: string,
  ): Promise<OpenAIPlatformKeySpend | undefined> {
    const baseUrl =
      await this.options.discoveryApi.getBaseUrl('ai-spend-monitor');
    const query = new URLSearchParams({ month });
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/openai-platform/key-spend?${query.toString()}`,
    );

    // 404 = the backend has no OpenAI Platform admin key configured. That is
    // an expected state, not a failure.
    if (response.status === 404) {
      return undefined;
    }
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json() as Promise<OpenAIPlatformKeySpend>;
  }
}
